/**
 * The shared ``<DataTable>`` pagination footer (ADR-038 Phase E;
 * REQ-UI-122).
 *
 * Item range, a windowed page-number strip with previous / next carets,
 * direct page-number entry, and a per-page size selector — modeled on the
 * Appaloosa ``Pagination`` reference implementation, in Meridian's markup
 * and tokens.
 *
 * Honest about bounded totals (D6): when the server reports the total as
 * a lower bound the strip is built from that bound and the label reads
 * "100,000+" rather than a precise-looking count.
 *
 * Cross-references:
 *   - REQ-UI-122 (page size, prev/next, direct page entry).
 *   - REQ-UI-059 (keyboard-operable controls, labelled navigation).
 *   - Feature spec §2.10 (pagination footer) / D6 (bounded counts).
 */
import { useEffect, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import {
  clampPageIndex,
  itemRangeLabel,
  pageCount,
  pageNumberWindow,
} from "@/data-table/data-table-model";
import { cn } from "@/lib/utils";

/** How many page numbers the strip shows at once. */
const PAGE_WINDOW_SIZE = 5;

export interface DataTablePaginationProps {
  /** Zero-based current page index. */
  readonly pageIndex: number;
  /** Current page size. */
  readonly pageSize: number;
  /** Rows on the current page — sizes the range label's upper bound. */
  readonly itemsOnPage: number;
  /** Result total, possibly a lower bound (see ``totalIsLowerBound``). */
  readonly total: number;
  /** Whether ``total`` is the server's count ceiling rather than an exact count. */
  readonly totalIsLowerBound: boolean;
  /** Selectable page sizes, served by the BFF catalog. */
  readonly pageSizeOptions: readonly number[];
  readonly onPageIndexChange: (next: number) => void;
  readonly onPageSizeChange: (next: number) => void;
  /** Test id root. */
  readonly testId?: string;
}

/**
 * Render the pagination footer.
 *
 * The direct page-entry field is uncontrolled-until-committed: it tracks
 * the operator's keystrokes locally and only calls back on Enter or
 * blur, so a half-typed "1" on the way to "12" doesn't fire a query.
 */
export function DataTablePagination({
  pageIndex,
  pageSize,
  itemsOnPage,
  total,
  totalIsLowerBound,
  pageSizeOptions,
  onPageIndexChange,
  onPageSizeChange,
  testId = "data-table-pagination",
}: DataTablePaginationProps): React.ReactElement {
  const count = pageCount(total, pageSize);
  const pages = pageNumberWindow(pageIndex, count, PAGE_WINDOW_SIZE);
  const [pageEntry, setPageEntry] = useState(String(pageIndex + 1));

  // Re-sync the entry field whenever the page moves by any other route
  // (caret, page-number button, a re-run that reset to page 1).
  useEffect(() => {
    setPageEntry(String(pageIndex + 1));
  }, [pageIndex]);

  const commitPageEntry = (): void => {
    const parsed = Number.parseInt(pageEntry, 10);
    if (Number.isNaN(parsed)) {
      setPageEntry(String(pageIndex + 1));
      return;
    }
    const next = clampPageIndex(parsed - 1, count);
    setPageEntry(String(next + 1));
    if (next !== pageIndex) onPageIndexChange(next);
  };

  return (
    <footer
      className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2 text-body-sm text-text-secondary"
      data-testid={testId}
    >
      <span data-testid={`${testId}-range`}>
        {itemRangeLabel(pageIndex, pageSize, itemsOnPage, total, totalIsLowerBound)}
      </span>

      <nav aria-label="Pagination" className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageIndexChange(pageIndex - 1)}
          disabled={pageIndex === 0}
          aria-label="Previous page"
          data-testid={`${testId}-prev`}
          className="rounded border border-border p-1 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
        >
          <CaretLeft size={12} aria-hidden="true" />
        </button>

        {pages.map((index) => (
          <button
            key={index}
            type="button"
            onClick={() => onPageIndexChange(index)}
            aria-label={`Page ${index + 1}`}
            aria-current={index === pageIndex ? "page" : undefined}
            data-testid={`${testId}-page-${index}`}
            className={cn(
              "min-w-6 rounded border px-1.5 py-0.5 tabular-nums",
              index === pageIndex
                ? "border-brand bg-bg-elevated text-text-primary"
                : "border-border text-text-secondary hover:bg-bg-elevated",
            )}
          >
            {index + 1}
          </button>
        ))}

        <button
          type="button"
          onClick={() => onPageIndexChange(pageIndex + 1)}
          disabled={pageIndex >= count - 1}
          aria-label="Next page"
          data-testid={`${testId}-next`}
          className="rounded border border-border p-1 text-text-primary hover:bg-bg-elevated disabled:opacity-40"
        >
          <CaretRight size={12} aria-hidden="true" />
        </button>
      </nav>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1">
          <span>Go to page</span>
          <input
            type="text"
            inputMode="numeric"
            value={pageEntry}
            onChange={(event) => setPageEntry(event.target.value)}
            onBlur={commitPageEntry}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitPageEntry();
              }
            }}
            data-testid={`${testId}-goto`}
            className="w-12 rounded border border-border bg-bg-base px-1 py-0.5 text-right tabular-nums text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          />
          <span className="text-text-muted">of {count.toLocaleString("en-US")}</span>
        </label>

        <label className="flex items-center gap-1">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            data-testid={`${testId}-page-size`}
            className="rounded border border-border bg-bg-base px-1 py-0.5 text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
    </footer>
  );
}
